import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuizResults = ({ results, onRetake, onViewRecommendations }) => {
  const navigate = useNavigate();

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-success';
    if (percentage >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getScoreBgColor = (percentage) => {
    if (percentage >= 80) return 'from-success/20 to-success/5';
    if (percentage >= 60) return 'from-warning/20 to-warning/5';
    return 'from-destructive/20 to-destructive/5';
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
          {/* Success Header */}
          <div className="text-center space-y-4">
            <div className={`inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full ${
              results?.validated ? 'bg-success/10' : 'bg-warning/10'
            }`}>
              <Icon 
                name={results?.validated ? "Trophy" : "AlertCircle"} 
                size={32} 
                color={results?.validated ? "var(--color-success)" : "var(--color-warning)"} 
              />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                {results?.validated ? 'Quiz Terminé!' : 'Quiz Non Validé'}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {results?.validated 
                  ? 'Félicitations pour avoir complété l\'évaluation' :'Score minimum requis: 70%'}
              </p>
            </div>
          </div>

          {/* Validation Feedback for Failed Attempts */}
          {!results?.validated && (
            <div className="bg-warning/10 border border-warning/30 rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center">
                  <Icon name="Info" size={20} color="var(--color-warning)" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Veuillez retenter le quiz
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Vous avez obtenu {results?.overallScore}% mais le score minimum requis est de 70%. 
                    Votre tentative a été enregistrée. Prenez le temps de réviser et réessayez!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Overall Score */}
          <div className={`bg-gradient-to-br ${getScoreBgColor(results?.overallScore)} rounded-2xl p-6 md:p-8`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <p className="text-sm md:text-base text-muted-foreground mb-2">Score Global</p>
                <div className="flex items-baseline gap-2">
                  <span className={`text-5xl md:text-6xl font-bold ${getScoreColor(results?.overallScore)} data-text`}>
                    {results?.overallScore}
                  </span>
                  <span className="text-2xl md:text-3xl text-muted-foreground">/100</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-foreground data-text">
                    {results?.correctAnswers}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground">Correctes</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-foreground data-text">
                    {results?.totalQuestions}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground">Total</p>
                </div>
              </div>
            </div>
          </div>

          {/* Strength Areas */}
          <div className="bg-card rounded-2xl border border-border p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Vos Points Forts
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {results?.strengths?.map((strength, index) => (
                <div key={index} className="bg-muted rounded-xl p-4 md:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
                      <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-semibold text-foreground mb-1">
                        {strength?.area}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-2 bg-background rounded-full overflow-hidden">
                          <div
                            className="h-full bg-success transition-all duration-500"
                            style={{ width: `${strength?.score}%` }}
                          />
                        </div>
                        <span className="text-xs md:text-sm font-medium text-success data-text">
                          {strength?.score}%
                        </span>
                      </div>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {strength?.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>


          {/* Points Earned */}
          <div className="bg-gradient-to-br from-accent/20 to-accent/5 rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-accent">
                  <Icon name="Star" size={24} color="#FFFFFF" />
                </div>
                <div>
                  <p className="text-sm md:text-base text-muted-foreground mb-1">
                    {results?.pointsMessage || 'Points Gagnés'}
                  </p>
                  <p className="text-2xl md:text-3xl font-bold text-accent data-text">
                    +{results?.pointsEarned} points
                  </p>
                </div>
              </div>
              {results?.badgeEarned && (
                <div className="flex items-center gap-3 px-4 py-3 bg-card rounded-xl border border-border">
                  <Icon name="Award" size={24} color="var(--color-accent)" />
                  <div>
                    <p className="text-xs text-muted-foreground">Badge Débloqué</p>
                    <p className="text-sm font-semibold text-foreground">
                      {results?.badgeEarned}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Button
              variant="outline"
              onClick={onRetake}
              iconName="RotateCcw"
              iconPosition="left"
              fullWidth
              className="sm:flex-1"
            >
              Refaire le Quiz
            </Button>
            <Button
              variant="default"
              onClick={onViewRecommendations}
              iconName="ArrowRight"
              iconPosition="right"
              fullWidth
              className="sm:flex-1"
            >
              Voir les Recommandations
            </Button>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/student-dashboard')}
              className="text-sm md:text-base text-primary hover:underline"
            >
              Retour au Tableau de Bord
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;